import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockUsers } from '../mockData';
import { getDashboardForRole, roleLabels } from '../clinical/rbac';
import { getInstitutionalTheme } from '../config/institutionalTheme';
import '../styles/Login.css';

const Login = ({ onLogin, portalType = 'mixed', sessionMessage = '' }) => {
  const navigate = useNavigate();
  const isDoctorPortal = portalType === 'doctor';
  const isPatientPortal = portalType === 'patient';
  const [theme, setTheme] = useState(() => getInstitutionalTheme());
  const title = isDoctorPortal ? `${theme.applicationName} ` : `${theme.applicationName} Pacientes`;
  const subtitle = isDoctorPortal
    ? 'Acceso privado para administrador, medico, enfermeria y admision'
    : 'Acceso publicado desde el portal institucional de la clinica';
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const demoMode = false;

  useEffect(() => {
    const syncTheme = (event) => {
      setTheme(event.detail || getInstitutionalTheme());
    };
    window.addEventListener('institutional-theme-updated', syncTheme);
    return () => window.removeEventListener('institutional-theme-updated', syncTheme);
  }, []);

  const handleUserSelect = (user, selectedRole = user.role) => {
    const userData = {
      id: user.id,
      name: user.name,
      role: selectedRole,
      demoMode,
      ...user,
    };
    onLogin(userData);
    navigate(getDashboardForRole(selectedRole));
  };

  const renderInternalTable = () => (
    <div className="patient-login-panel">
      <form className="credential-form" onSubmit={(event) => {
        event.preventDefault();
        const internalUsers = [...mockUsers.staff, ...mockUsers.doctors];
        const staffUser = internalUsers.find((candidate) => (
          candidate.username === credentials.username.trim() &&
          candidate.password === credentials.password
        ));

        if (!staffUser) {
          setLoginError('Usuario o contrasena incorrectos.');
          return;
        }

        setLoginError('');
        handleUserSelect(staffUser, staffUser.role);
      }}>
        <label>
          Usuario
          <input
            type="text"
            value={credentials.username}
            onChange={(event) => setCredentials((current) => ({ ...current, username: event.target.value }))}
            placeholder="Ej. general"
            autoComplete="username"
          />
        </label>
        <label>
          Contrasena
          <input
            type="password"
            value={credentials.password}
            onChange={(event) => setCredentials((current) => ({ ...current, password: event.target.value }))}
            placeholder="Ej. 1234"
            autoComplete="current-password"
          />
        </label>
        
        {loginError && <p className="login-error">{loginError}</p>}
        <button className="login-submit" type="submit">Ingresar a plataforma</button>
      </form>
      <div className="login-help">
        <strong>Credenciales de acceso</strong>
        <span>Administrador: admin / 1234</span>
        <span>Medicina general: general / 1234</span>
        <span>Enfermeria: enfermeria / 1234</span>
        <span>Admision: admision / 1234</span>
      </div>
    </div>
  );

  const renderPatientTable = () => (
    <div className="patient-login-panel">
      <form className="credential-form" onSubmit={(event) => {
        event.preventDefault();
        const patient = mockUsers.patients.find((candidate) => (
          candidate.username === credentials.username.trim() &&
          candidate.password === credentials.password
        ));

        if (!patient) {
          setLoginError('Usuario o contrasena incorrectos.');
          return;
        }

        if (!patient.serviceActive) {
          setLoginError('El servicio del paciente no esta activo. Contacta a la clinica.');
          return;
        }

        setLoginError('');
        handleUserSelect(patient, 'patient');
      }}>
        <label>
          Usuario
          <input
            type="text"
            value={credentials.username}
            onChange={(event) => setCredentials((current) => ({ ...current, username: event.target.value }))}
            placeholder="Ej. lucia"
            autoComplete="username"
          />
        </label>
        <label>
          Contrasena
          <input
            type="password"
            value={credentials.password}
            onChange={(event) => setCredentials((current) => ({ ...current, password: event.target.value }))}
            placeholder="Ej. 1234"
            autoComplete="current-password"
          />
        </label>
        
        {loginError && <p className="login-error">{loginError}</p>}
        <button className="login-submit" type="submit">Ingresar al portal</button>
      </form>
      <div className="login-help">
        <strong>Credenciales de acceso</strong>
        <span>Usuario: lucia</span>
        <span>Contrasena: 1234</span>
      </div>
    </div>
  );

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className={`login-logo ${theme.logoUrl ? 'with-image' : ''}`}>
            {theme.logoUrl ? (
              <img src={theme.logoUrl} alt={theme.institutionName} />
            ) : (
              theme.logoText
            )}
          </div>
          <h1>{title}</h1>
          <p className="login-subtitle">{subtitle}</p>
        </div>

        {sessionMessage && (
          <div className="session-message">
            {sessionMessage}
          </div>
        )}

        {portalType === 'mixed' ? (
          <div className="role-selection">
            <h2>Selecciona tu rol</h2>
            <div className="role-buttons">
              <button
                className="role-btn doctor-btn"
                onClick={() => navigate('/plataforma')}
              >
                <span className="role-icon">MED</span>
                <span className="role-label">Equipo interno</span>
              </button>
              <button
                className="role-btn patient-btn"
                onClick={() => navigate('/portal')}
              >
                <span className="role-icon">PAC</span>
                <span className="role-label">Paciente</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="user-selection">
            <h2>{isPatientPortal ? 'Acceso de pacientes' : 'Acceso interno de medicos'}</h2>
            {isPatientPortal ? renderPatientTable() : renderInternalTable()}
          </div>
        )}

        <div className="login-footer">
          <p>{isDoctorPortal ? `Roles: ${Object.values(roleLabels).filter((label) => label !== 'Paciente').join(', ')}` : 'Portal visible para pacientes'}</p>
          <small>{isDoctorPortal ? 'URL interna: /plataforma' : 'URL institucional: /portal'}</small>
        </div>
      </div>
    </div>
  );
};

export default Login;
